import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  SimpleGrid,
  Card,
  Stat,
  Badge,
  Table,
  Icon,
} from '@chakra-ui/react';
import { FiDollarSign, FiTrendingUp } from 'react-icons/fi';
import { adminAPI } from '../services/api';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { toaster } from './ui/toaster';
import { useTranslation } from 'react-i18next';

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  tier?: string;
  monthly_price: number;
  annual_price: number;
  reviews_per_month: number;
  max_document_size_mb: number;
  max_documents_per_review: number;
  report_storage_days: number;
  unlimited_standards: boolean;
  features: Record<string, any>;
  is_active: boolean;
}

interface UserSubscription {
  id: number;
  email: string;
  subscription_tier: string;
  subscription_status: string;
  created_at: string;
  subscription_started_at?: string | null;
  subscription_ends_at?: string | null;
}

const SubscriptionManagement: React.FC = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [users, setUsers] = useState<UserSubscription[]>([]);
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  
  const cardBg = useColorModeValue('white', 'gray.800');

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, usersRes, dashRes] = await Promise.all([
        adminAPI.getSubscriptionPlans(),
        adminAPI.getUsers({ subscription_tier: 'all' }),
        adminAPI.getDashboardStats(),
      ]);
      
      setPlans((plansRes && plansRes.plans) || []);
      setUsers(usersRes || []);
      setDashboardStats(dashRes);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toaster.create({
        title: t('admin.content.subscriptionManagement.toasts.errorLoadingDataTitle'),
        description: t('admin.content.subscriptionManagement.toasts.failedToLoadSubscriptionData'),
        type: 'error',
      });
    }
  };



  const getSubscriptionStats = () => {
    // Get user counts by tier dynamically
    const usersByTier = users.reduce((acc, user) => {
      acc[user.subscription_tier] = (acc[user.subscription_tier] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    // Use billing data directly from dashboard stats (server-calculated)
    let totalMRR = dashboardStats?.billing?.total_mrr / 100 || 0; // Convert from cents
    let mrr_by_tier = dashboardStats?.billing?.mrr_by_tier || {};
    
    // Build planStats from billing data
    const planStats: Record<string, { count: number; revenue: number; plan: any }> = {};

    Object.entries(mrr_by_tier).forEach(([tier, tierData]: [string, any]) => {
      const plan = plans.find(p => p.tier === tier);
      if (plan) {
        planStats[plan.name] = {
          count: tierData.count,
          revenue: tierData.tier_mrr / 100, // Convert from cents
          plan
        };
      }
    });

    return { 
      usersByTier,
      planStats,
      totalMRR,
      totalPaidUsers: dashboardStats?.billing?.active_subscriptions || 0
    };
  };

  const statsData = getSubscriptionStats();


  const getPlanDisplayName = (plan?: Partial<SubscriptionPlan> | null, fallback?: string) => {
    const tier = plan?.tier;
    switch (tier) {
      case 'FREE_TRIAL': return t('admin.content.common.tiers.freeTrial');
      case 'STARTER': return t('admin.content.common.tiers.starter');
      case 'PRO': return t('admin.content.common.tiers.pro');
      case 'BUSINESS': return t('admin.content.common.tiers.business');
      default:
        if ((fallback || plan?.name) === 'Free Trial') return t('admin.content.common.tiers.freeTrial');
        return fallback || plan?.name || t('admin.content.common.notSet');
    }
  };

  const getSubscriptionStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return t('admin.content.common.status.active');
      case 'inactive': return t('admin.content.common.status.inactive');
      case 'pending': return t('admin.content.common.status.pending');
      case 'suspended': return t('admin.content.common.status.suspended');
      default: return status || t('admin.content.common.notSet');
    }
  };

  // Resolve a user's tier code to its plan via the plans list.
  // Plans are the single source of truth — no hardcoded tier names.
  const getPlanForTier = (tier: string) => plans.find(p => p.tier === tier);

  return (
    <VStack gap={8} align="stretch">
      {/* Header */}
      <Box>
        <Heading size="lg" mb={2} color="black">{t('admin.content.subscriptionManagement.title')}</Heading>
        <Text color="gray.600" fontSize="sm">
          {t('admin.content.subscriptionManagement.subtitle')}
        </Text>
      </Box>
      
      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
        {/* Dynamic subscription tier cards */}
        {Object.entries(statsData.planStats).map(([planName, planData]) => (
          <Card.Root key={planName} bg={cardBg} shadow="lg" borderRadius="xl" border="1px solid" borderColor={
            planName === 'Free Trial' ? 'gray.200' : 
            planName === 'Pro' ? 'blue.200' : 'purple.200'
          }>
            <Card.Body>
              <Stat.Root>
                <Stat.Label color={
                  planName === 'Free Trial' ? 'gray.500' : 
                  planName === 'Pro' ? 'blue.500' : 'purple.500'
                } fontSize="sm" fontWeight="medium">{getPlanDisplayName(planData.plan, planName)}</Stat.Label>
                <Stat.ValueText fontSize="2xl" fontWeight="bold" color={
                  planName === 'Free Trial' ? 'gray.600' : 
                  planName === 'Pro' ? 'blue.600' : 'purple.600'
                }>
                  {planData.count}
                </Stat.ValueText>
                <Stat.HelpText>
                  {planName === 'Free Trial' ? (
                    <>
                      {t('admin.content.subscriptionManagement.metrics.percentOfTotal', { percent: users.length > 0 ? Math.round((planData.count / users.length) * 100) : 0 })}
                    </>
                  ) : (
                    <>
                      {t('admin.content.subscriptionManagement.metrics.monthlyRevenueChange', { amount: planData.revenue })}
                    </>
                  )}
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        ))}
        
        <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" border="1px solid" borderColor="green.200">
          <Card.Body>
            <Stat.Root>
              <Stat.Label color="green.500" fontSize="sm" fontWeight="medium">{t('admin.content.subscriptionManagement.metrics.totalMrr')}</Stat.Label>
              <Stat.ValueText fontSize="2xl" fontWeight="bold" color="green.600">
                ${statsData.totalMRR.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Stat.ValueText>
              <Stat.HelpText>
                {t('admin.content.subscriptionManagement.metrics.conversionRateWithPercent', { percent: users.length > 0 ? Math.round((statsData.totalPaidUsers / users.length) * 100) : 0 })}
              </Stat.HelpText>
            </Stat.Root>
          </Card.Body>
        </Card.Root>
      </SimpleGrid>

      {/* Payments & Usage Tracking */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
        {/* Payment History */}
        <Card.Root bg={cardBg} shadow="lg" borderRadius="xl">
          <Card.Body>
            <HStack justify="space-between" mb={4}>
              <Box>
                <Text fontSize="xl" fontWeight="bold" color="black">{t('admin.content.subscriptionManagement.paymentHistory.title')}</Text>
                <Text color="gray.600" fontSize="sm">{t('admin.content.subscriptionManagement.paymentHistory.subtitle')}</Text>
              </Box>
              <Icon as={FiDollarSign} color="green.500" boxSize={6} />
            </HStack>
            
            <VStack gap={3} align="stretch">
              {users.filter(user => ((getPlanForTier(user.subscription_tier)?.monthly_price ?? 0) > 0)).slice(0, 5).map((user) => {
                const plan = getPlanForTier(user.subscription_tier);
                return (
                <HStack key={user.id} justify="space-between" p={3} bg="gray.50" borderRadius="md">
                  <VStack align="start" gap={1}>
                    <Text fontSize="sm" fontWeight="medium">{user.email}</Text>
                    <Text fontSize="xs" color="gray.600">
                      {getPlanDisplayName(plan)}
                    </Text>
                  </VStack>
                  <VStack align="end" gap={1}>
                    <Text fontSize="sm" fontWeight="bold" color="green.600">
                      {t('admin.content.subscriptionManagement.paymentHistory.pricePerMonth', { amount: ((plan?.monthly_price ?? 0) / 100).toFixed(0) })}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {user.subscription_started_at ? new Date(user.subscription_started_at).toLocaleDateString() : 
                       user.created_at ? t('admin.content.subscriptionManagement.paymentHistory.joinedDate', { date: new Date(user.created_at).toLocaleDateString() }) : t('admin.content.common.na')}
                    </Text>
                  </VStack>
                </HStack>
                );
              })}
              {users.filter(user => ((getPlanForTier(user.subscription_tier)?.monthly_price ?? 0) > 0)).length === 0 && (
                <Text color="gray.500" textAlign="center" py={4}>
                  {t('admin.content.subscriptionManagement.paymentHistory.empty')}
                </Text>
              )}
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* Usage Analytics */}
        <Card.Root bg={cardBg} shadow="lg" borderRadius="xl">
          <Card.Body>
            <HStack justify="space-between" mb={4}>
              <Box>
                <Text fontSize="xl" fontWeight="bold" color="black">{t('admin.content.subscriptionManagement.usageAnalytics.title')}</Text>
                <Text color="gray.600" fontSize="sm">{t('admin.content.subscriptionManagement.usageAnalytics.subtitle')}</Text>
              </Box>
              <Icon as={FiTrendingUp} color="blue.500" boxSize={6} />
            </HStack>
            
            <VStack gap={4} align="stretch">
              {/* Usage by Tier */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>{t('admin.content.subscriptionManagement.usageAnalytics.usageByTier')}</Text>
                <VStack gap={2}>
                  {Object.entries(statsData.planStats).map(([planName, planData]) => (
                    <HStack key={planName} justify="space-between" w="100%">
                      <Text fontSize="sm">{getPlanDisplayName(planData.plan, planName)}</Text>
                      <HStack gap={2}>
                        <Text fontSize="sm" fontWeight="medium">{t('admin.content.subscriptionManagement.usageAnalytics.usersCount', { count: planData.count })}</Text>
                        {(planData.plan?.monthly_price ?? 0) > 0 && (
                          <Badge colorScheme="green" size="sm">
                            {t('admin.content.subscriptionManagement.usageAnalytics.revenuePerMonthShort', { amount: planData.revenue })}
                          </Badge>
                        )}
                      </HStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              
              {/* Conversion Metrics */}
              <Box pt={2} borderTop="1px solid" borderColor="gray.200">
                <Text fontSize="sm" fontWeight="medium" mb={2}>{t('admin.content.subscriptionManagement.usageAnalytics.conversionMetrics')}</Text>
                <SimpleGrid columns={2} gap={4}>
                  <Box textAlign="center">
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">
                      {users.length > 0 ? Math.round((statsData.totalPaidUsers / users.length) * 100) : 0}%
                    </Text>
                    <Text fontSize="xs" color="gray.600">{t('admin.content.subscriptionManagement.usageAnalytics.conversionRate')}</Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="lg" fontWeight="bold" color="green.600">
                      ${statsData.totalMRR.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                    <Text fontSize="xs" color="gray.600">{t('admin.content.subscriptionManagement.usageAnalytics.monthlyRevenue')}</Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>
      </SimpleGrid>

      {/* User Subscriptions Table */}
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl">
        <Card.Body>
          <Text fontSize="xl" fontWeight="bold" mb={4} color="black">{t('admin.content.subscriptionManagement.userSubscriptions.title')}</Text>
          <Table.Root variant="line">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>{t('admin.content.userManagement.fields.email')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('admin.content.subscriptionManagement.userSubscriptions.tier')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('admin.content.subscriptionManagement.userSubscriptions.status')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('admin.content.subscriptionManagement.userSubscriptions.startDate')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('admin.content.subscriptionManagement.userSubscriptions.endDate')}</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.map((user) => (
                <Table.Row key={user.id}>
                  <Table.Cell>{user.email}</Table.Cell>
                  <Table.Cell>{getPlanDisplayName(getPlanForTier(user.subscription_tier))}</Table.Cell>
                  <Table.Cell>{getSubscriptionStatusLabel(user.subscription_status)}</Table.Cell>
                  <Table.Cell>{user.subscription_started_at ? new Date(user.subscription_started_at).toLocaleDateString() : 
                       user.created_at ? t('admin.content.subscriptionManagement.paymentHistory.joinedDate', { date: new Date(user.created_at).toLocaleDateString() }) : t('admin.content.common.notSet')}</Table.Cell>
                  <Table.Cell>{user.subscription_ends_at ? new Date(user.subscription_ends_at).toLocaleDateString() : 
                       user.subscription_tier === 'FREE_TRIAL' ? 
                         (user.created_at ? new Date(new Date(user.created_at).getTime() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString() : t('admin.content.common.notSet')) :
                         t('admin.content.common.notSet')}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>

    </VStack>
  );
};

export default SubscriptionManagement;

